import { Icon, type IconType } from "materia"
import type { ReactNode } from "react"
import { modifiers } from "ui-tools"
import {
  MAX_ZOOM_FACTOR,
  MIN_ZOOM_FACTOR,
  usePDFContext,
} from "../../providers"
import s from "./Toolbar.module.scss"

type ToolbarProps = { fileName: string; downloadUrl: string | null }
export const Toolbar = ({ fileName, downloadUrl }: ToolbarProps) => {
  const { isOutlineVisible, toggleOutline } = usePDFContext()

  return (
    <div className={s.toolbar}>
      <ToolbarSection>
        <ToolbarButton
          icon="List"
          title="Show PDF outline"
          onClick={toggleOutline}
          isActive={isOutlineVisible}
        />

        <div className={s.toolbar__title} title={fileName}>
          {fileName}
        </div>
      </ToolbarSection>

      <ToolbarSection>
        <NavigationControls />
        <div className={s.toolbar__separator} />
        <ZoomControls />
        <div className={s.toolbar__separator} />
        <FitControls />
      </ToolbarSection>

      <ToolbarSection>
        {downloadUrl && <DownloadLink downloadUrl={downloadUrl} />}
      </ToolbarSection>
    </div>
  )
}

const NavigationControls = () => {
  const { currentPageNumber, numberOfPagesRef, navigateToPage } =
    usePDFContext()

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPage = e.target.value
    navigateToPage(Number(newPage))
  }

  return (
    <ToolbarSection>
      <div className={s.toolbar__pages}>
        <input
          className={s.toolbar__pageInput}
          value={currentPageNumber}
          type="number"
          min={1}
          max={numberOfPagesRef.current}
          onChange={onChange}
          onFocus={(e) => e.target.select()}
        />
        <span className={s.toolbar__numberOfPages}>/</span>
        <span className={s.toolbar__numberOfPages}>
          {numberOfPagesRef.current}
        </span>
      </div>

      <ToolbarButton
        icon="ChevronLight"
        iconRotate={90}
        disabled={currentPageNumber >= numberOfPagesRef.current}
        title="Navigate to next page"
        onClick={() => navigateToPage(currentPageNumber + 1)}
      />

      <ToolbarButton
        disabled={currentPageNumber <= 1}
        icon="ChevronLight"
        iconRotate={270}
        title="Navigate to previous page"
        onClick={() => navigateToPage(currentPageNumber - 1)}
      />
    </ToolbarSection>
  )
}

const ZoomControls = () => {
  const { zoomFactor, zoomIn, zoomOut } = usePDFContext()

  const zoomPercentage = `${Math.round(zoomFactor * 100)}%`

  return (
    <ToolbarSection>
      <ToolbarButton
        disabled={zoomFactor <= MIN_ZOOM_FACTOR}
        icon="Minus"
        title="Zoom out"
        onClick={zoomOut}
      />

      <input className={s.toolbar__zoomInput} value={zoomPercentage} disabled />

      <ToolbarButton
        disabled={zoomFactor >= MAX_ZOOM_FACTOR}
        icon="Plus"
        title="Zoom in"
        onClick={zoomIn}
      />
    </ToolbarSection>
  )
}

const FitControls = () => {
  const { fitToWidth, fitToHeight } = usePDFContext()

  return (
    <ToolbarSection>
      <ToolbarButton
        icon="DoubleArrow"
        iconRotate={90}
        title="Fit to width"
        onClick={fitToWidth}
      />
      <ToolbarButton
        icon="DoubleArrow"
        title="Fit to height"
        onClick={fitToHeight}
      />
    </ToolbarSection>
  )
}

const DownloadLink = ({ downloadUrl }: { downloadUrl: string }) => {
  return (
    <a
      href={downloadUrl}
      className={s.toolbarButton}
      title="Download PDF"
      download
    >
      <Icon icon="Download" size="medium" />
    </a>
  )
}

const ToolbarSection = ({ children }: { children: ReactNode }) => {
  return <div className={s.toolbar__section}>{children}</div>
}

const ToolbarButton = ({
  icon,
  iconRotate,
  isActive = false,
  ...buttonProps
}: {
  icon?: IconType
  iconRotate?: 0 | 90 | 180 | 270
  isActive?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={modifiers(s, "toolbarButton", { isActive })}
      {...buttonProps}
    >
      {icon && <Icon icon={icon} size="medium" rotate={iconRotate} />}
    </button>
  )
}
